import { HttpTypes } from "@medusajs/types"

type ProductLike = HttpTypes.StoreProduct & {
  images?: HttpTypes.StoreProduct["images"]
}

const U = (id: string) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=1200&q=80`

const GALLERY_OFFROAD = [
  U("photo-1533473359331-0135ef1b58bf"),
  U("photo-1606664515524-ed2f786a0bd6"),
  U("photo-1519641471654-76ce0107ad1b"),
  U("photo-1469854523086-cc02fe5d8800"),
]

const GALLERY_CAMP = [
  U("photo-1504280390367-361c6d9f38f4"),
  U("photo-1523987355523-c7b5b0dd90a7"),
  U("photo-1470246973918-29a93221c455"),
  U("photo-1441974231531-c6227db76b6e"),
]

function hashHandle(handle: string) {
  let h = 0
  for (let i = 0; i < handle.length; i++) {
    h = (h * 31 + handle.charCodeAt(i)) >>> 0
  }
  return h
}

function isCampProduct(product: ProductLike) {
  const key = `${product.handle || ""} ${product.title || ""}`
  return /camp|tent|sleep|lantern|stove|chair|awning|bag|dutch|table/i.test(key)
}

function buildFallbackDescription(product: ProductLike) {
  const title = product.title || "This outdoor essential"
  return [
    `${title} is built for real trail days—durable materials, considered details, and a form that earns its place in the kit.`,
    "Designed for overland and camping use across seasons: weather-ready, easy to pack, and dependable when the route turns rough.",
    "Pair it with the rest of your recovery or camp setup for a cleaner, more reliable base on every trip.",
  ].join("\n\n")
}

function buildFallbackImages(product: ProductLike) {
  const pool = isCampProduct(product) ? GALLERY_CAMP : GALLERY_OFFROAD
  const start = hashHandle(product.handle || product.id || "ru") % pool.length
  const urls = [0, 1, 2, 3].map((i) => pool[(start + i) % pool.length])
  if (product.thumbnail && !urls.includes(product.thumbnail)) {
    urls[0] = product.thumbnail
  }
  return urls.map((url, rank) => ({
    id: `fallback-${product.id || product.handle}-${rank}`,
    url,
    rank,
  })) as NonNullable<HttpTypes.StoreProduct["images"]>
}

/**
 * Ensure PDP always has usable gallery + long-form description.
 * Fills gaps only — never replaces existing rich content.
 */
export function ensureProductDetailContent<T extends ProductLike>(product: T): T {
  const images = product.images?.length
    ? product.images
    : product.thumbnail
      ? [
          {
            id: "thumbnail",
            url: product.thumbnail,
            rank: 0,
          } as NonNullable<HttpTypes.StoreProduct["images"]>[number],
        ]
      : []

  const needsGallery = images.length < 2
  const desc = (product.description || "").trim()
  const needsDescription = desc.length < 160

  const fallbackGallery = buildFallbackImages(product)
  const mergedImages = needsGallery
    ? [
        ...images,
        ...fallbackGallery.filter(
          (img) => !images.some((existing) => existing.url === img.url)
        ),
      ].slice(0, 4)
    : images

  return {
    ...product,
    description: needsDescription
      ? buildFallbackDescription(product)
      : product.description,
    images: mergedImages,
  }
}
